// Pure moves over a template's sections.
//
// The builder's buttons, its Move-to-section dialog AND the drag handles all
// call these — one set of functions, so the gesture and the button cannot
// disagree about what a move means, and jsdom can pin the logic without
// simulating a drag. Every function returns a NEW sections array (the
// builder's content document is controlled state).
import type { FormContent, FormField } from '../formsApi';

type Sections = FormContent['sections'];

/** Swap a question with its neighbour. No-op at either end. */
export function swapFields(sections: Sections, si: number, fi: number, by: -1 | 1): Sections {
  const target = fi + by;
  const fields = sections[si]?.fields;
  if (!fields || target < 0 || target >= fields.length) return sections;
  const next = [...fields];
  [next[fi], next[target]] = [next[target]!, next[fi]!];
  return sections.map((s, i) => (i === si ? { ...s, fields: next } : s));
}

/** Reorder within one section (the drag's landing). */
export function reorderFields(sections: Sections, si: number, from: number, to: number): Sections {
  const fields = sections[si]?.fields;
  if (!fields || from === to || from < 0 || to < 0 || from >= fields.length || to >= fields.length) {
    return sections;
  }
  const next = [...fields];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return sections.map((s, i) => (i === si ? { ...s, fields: next } : s));
}

/** Swap a whole section with its neighbour. */
export function swapSections(sections: Sections, si: number, by: -1 | 1): Sections {
  const target = si + by;
  if (target < 0 || target >= sections.length) return sections;
  const next = [...sections];
  [next[si], next[target]] = [next[target]!, next[si]!];
  return next;
}

/** Reorder sections (the drag's landing). */
export function reorderSections(sections: Sections, from: number, to: number): Sections {
  if (from === to || from < 0 || to < 0 || from >= sections.length || to >= sections.length) {
    return sections;
  }
  const next = [...sections];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/** Move a question to the END of another section. */
export function moveFieldToSection(
  sections: Sections,
  fromSi: number,
  fi: number,
  toSi: number
): Sections {
  const field = sections[fromSi]?.fields[fi];
  if (!field || fromSi === toSi || !sections[toSi]) return sections;
  return sections.map((s, i) => {
    if (i === fromSi) return { ...s, fields: s.fields.filter((_, j) => j !== fi) };
    if (i === toSi) return { ...s, fields: [...s.fields, field] };
    return s;
  });
}

/**
 * A copy of a question, inserted directly below its source.
 *
 * The key comes IN rather than being generated here: a key is the join
 * between a question and every stored answer, so the one rule that matters
 * (the copy's key is fresh, the source's key is untouched) is pinned by a
 * test that hands this function a key it can see.
 */
export function duplicateField(
  sections: Sections,
  si: number,
  fi: number,
  key: string
): Sections {
  const source = sections[si]?.fields[fi];
  if (!source) return sections;
  const copy: FormField = structuredClone(source);
  copy.key = key;
  return sections.map((s, i) =>
    i === si
      ? { ...s, fields: [...s.fields.slice(0, fi + 1), copy, ...s.fields.slice(fi + 1)] }
      : s
  );
}
